import { useState, useEffect } from 'react';
import { adminApi, type Notice } from '@/lib/admin';
import { useToast } from '@/components/base/Toast';
import { SectionCard, EmptyState, LoadingState, ErrorState, ConfirmDialog } from '@/pages/admin/components/UI';

interface FormState {
  tag: string;
  title: string;
  summary: string;
  date: string;
  show_on_marquee: boolean;
}

const emptyForm: FormState = { tag: '公告', title: '', summary: '', date: '', show_on_marquee: false };

function todayStr() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function AdminNotices() {
  const { showToast } = useToast();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Notice | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Notice | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await adminApi.listNotices();
      setNotices(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, date: todayStr() });
    setModalOpen(true);
  };

  const openEdit = (n: Notice) => {
    setEditing(n);
    setForm({
      tag: n.tag,
      title: n.title,
      summary: n.summary,
      date: n.date,
      show_on_marquee: n.show_on_marquee,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.summary.trim()) {
      showToast('标题和内容不能为空', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await adminApi.updateNotice({ id: editing.id, ...form });
        showToast('公告已更新', 'success');
      } else {
        await adminApi.createNotice(form);
        showToast('公告已发布', 'success');
      }
      setModalOpen(false);
      await fetchData();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminApi.deleteNotice(deleteTarget.id);
      showToast('公告已删除', 'success');
      setDeleteTarget(null);
      await fetchData();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '删除失败', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-brown-900 text-lg font-bold">公告管理</h2>
          <p className="text-brown-400 text-xs mt-0.5">共 {notices.length} 条公告</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors self-start"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base"></i>
          </span>
          新增公告
        </button>
      </div>

      <SectionCard>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState onRetry={fetchData} />
        ) : notices.length === 0 ? (
          <EmptyState title="暂无公告" desc="点击右上角新增公告" />
        ) : (
          <div className="divide-y divide-brown-50">
            {notices.map((n) => (
              <div key={n.id} className="px-4 md:px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 hover:bg-brown-50/40 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gold-100 text-gold-700 text-xs font-medium whitespace-nowrap">
                      {n.tag || '公告'}
                    </span>
                    <span className="text-brown-400 text-xs">{n.date}</span>
                    {n.show_on_marquee && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium whitespace-nowrap">
                        <i className="ri-volume-up-line text-[10px] mr-0.5"></i>
                        行情页
                      </span>
                    )}
                  </div>
                  <h4 className="text-brown-900 text-sm font-bold truncate">{n.title}</h4>
                  <p className="text-brown-500 text-xs mt-1 line-clamp-2">{n.summary}</p>
                </div>
                <div className="flex items-center gap-2 md:flex-shrink-0">
                  <button
                    onClick={() => openEdit(n)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-brown-200 text-brown-600 text-xs cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors"
                  >
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-edit-line text-xs"></i>
                    </span>
                    编辑
                  </button>
                  <button
                    onClick={() => setDeleteTarget(n)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-100 text-red-500 text-xs cursor-pointer whitespace-nowrap hover:bg-red-50 transition-colors"
                  >
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-delete-bin-line text-xs"></i>
                    </span>
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* 新增/编辑弹窗 */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setModalOpen(false)}></div>
          <div className="relative bg-white rounded-xl w-full max-w-lg p-5 animate-fade-up max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-brown-900 text-base font-bold">
                {editing ? '编辑公告' : '新增公告'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-brown-400 hover:bg-brown-50 rounded-full cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-brown-600 text-xs mb-1.5">标签</label>
                <input
                  type="text"
                  value={form.tag}
                  onChange={(e) => setForm({ ...form, tag: e.target.value })}
                  placeholder="如：最新公告 / 通知"
                  className="w-full px-3 py-2.5 rounded-lg border border-brown-200 text-sm text-brown-800 outline-none focus:ring-2 focus:ring-gold-400"
                />
              </div>
              <div>
                <label className="block text-brown-600 text-xs mb-1.5">标题</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="请输入公告标题"
                  className="w-full px-3 py-2.5 rounded-lg border border-brown-200 text-sm text-brown-800 outline-none focus:ring-2 focus:ring-gold-400"
                />
              </div>
              <div>
                <label className="block text-brown-600 text-xs mb-1.5">内容</label>
                <textarea
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  rows={4}
                  maxLength={500}
                  placeholder="请输入公告内容"
                  className="w-full px-3 py-2.5 rounded-lg border border-brown-200 text-sm text-brown-800 outline-none focus:ring-2 focus:ring-gold-400 resize-none"
                />
                <p className="text-brown-300 text-[11px] mt-1 text-right">{form.summary.length}/500</p>
              </div>
              <div>
                <label className="block text-brown-600 text-xs mb-1.5">日期</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-brown-200 text-sm text-brown-800 outline-none focus:ring-2 focus:ring-gold-400"
                />
              </div>

              {/* 显示在行情页开关 */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brown-900 text-sm font-medium">显示在行情页跑马灯</p>
                  <p className="text-brown-400 text-xs mt-0.5">开启后该公告会在行情页顶部滚动展示</p>
                </div>
                <button
                  onClick={() => setForm({ ...form, show_on_marquee: !form.show_on_marquee })}
                  className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${
                    form.show_on_marquee ? 'bg-gold-500' : 'bg-brown-200'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform ${
                      form.show_on_marquee ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setModalOpen(false)}
                className="flex-1 py-2.5 rounded-lg border border-brown-200 text-brown-600 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 rounded-lg bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors disabled:opacity-60"
              >
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="确认删除该公告？"
        desc="删除后无法恢复，请谨慎操作。"
        confirmText="删除"
        danger
        loading={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}